/**
 * Shared plumbing for the "Copy From" control on the contact steps (title, escrow, settlement agent).
 * The contact steps all capture the same company/contact fields, so a snapshot of one step can fill another.
 */
export interface CdContactSnapshot {
  companyName: string;
  companyLicenseId: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  contactName: string;
  contactLicense: string;
  contactEmail: string;
  contactPhone: string;
}

export type CdContactCopySourceKey = 'sellerAgent' | 'buyerAgent' | 'titleInfo' | 'escrowInfo' | 'settlementAgent';

export interface CdContactCopySource {
  key: CdContactCopySourceKey;
  label: string;
  snapshot: CdContactSnapshot;
}

/** Implemented by a wizard that can offer the contact data already entered on its other steps. */
export interface CdContactCopySourceProvider {
  getContactCopySources(excludeKey: CdContactCopySourceKey): CdContactCopySource[];
}

export function hasAnyContactValue(snapshot: CdContactSnapshot): boolean {
  return Object.values(snapshot).some(value => (value || '').trim() !== '');
}

export function buildContactCopySource(
  key: CdContactCopySourceKey,
  label: string,
  snapshot: CdContactSnapshot
): CdContactCopySource | null {
  if (!hasAnyContactValue(snapshot)) {
    return null;
  }

  // Show who the party is so similarly named sections stay distinguishable.
  const describedBy = (snapshot.companyName || snapshot.contactName || '').trim();
  return {
    key,
    label: describedBy ? `${label} — ${describedBy}` : label,
    snapshot
  };
}
